/**
 * Main script.
 *
 * @author Simon Lagerlöf <contact@smn.codes>
 * @copyright 2024 Simon Lagerlöf
 * @since 1.0.0
 */

import barba from '@barba/core';
import Privcy from 'privcy';
import SiteHeader from './site/header';
import partialAccordion from './partials/accordion';
import partialHero from './partials/hero';

declare global {
  interface Window {
    privacyPopupStrings?: {
      title?: string;
      text?: string;
      categories?: {
        analytics?: {
          title?: string;
          text?: string;
        };
        social?: {
          title?: string;
          text?: string;
        };
      };
      strings: {
        categories: {
          enable: string;
        };
        buttons: {
          acceptAll: string;
          rejectAll: string;
          customize: string;
          saveSettings: string;
          back: string;
        };
      };
    };
  }
}

(async () => {
  const header = new SiteHeader();
  partialHero();
  partialAccordion();

  const privcyStrings = window.privacyPopupStrings;

  const privcy = new Privcy({
    title: privcyStrings?.title ?? '',
    description: privcyStrings?.text ?? '',
    categories: {
      analytics: {
        name: privcyStrings?.categories?.analytics?.title ?? '',
        description: privcyStrings?.categories?.analytics?.text ?? '',
      },
      social: {
        name: privcyStrings?.categories?.social?.title ?? '',
        description: privcyStrings?.categories?.social?.text ?? '',
      },
    },
    strings: {
      categories: {
        enable: privcyStrings?.strings.categories.enable ?? '',
      },
      buttons: {
        acceptAll: privcyStrings?.strings.buttons.acceptAll ?? '',
        rejectAll: privcyStrings?.strings.buttons.rejectAll ?? '',
        customize: privcyStrings?.strings.buttons.customize ?? '',
        saveSettings:
          privcyStrings?.strings.buttons.saveSettings ?? '',
        back: privcyStrings?.strings.buttons.back ?? '',
      },
    },
  });

  barba.init({
    schema: {
      prefix: 'barba',
    },
    transitions: [
      {
        enter() {},
        beforeEnter: ({ next }) => {
          const nextHtml = new DOMParser().parseFromString(
            next.html,
            'text/html',
          );

          if (!nextHtml) {
            return;
          }

          document.body.setAttribute(
            'class',
            nextHtml.body.classList.toString() ?? '',
          );

          document.body.setAttribute(
            'data-template',
            nextHtml.body.getAttribute('data-template') ?? '',
          );

          document.head.querySelector('style#page-theme')?.remove();
          const pageTheme = nextHtml.head.querySelector(
            'style#page-theme',
          );
          if (pageTheme) {
            document.head.appendChild(pageTheme);
          }

          const header = nextHtml.querySelector('header.site-header');

          if (header) {
            document
              .querySelector('header.site-header')
              ?.replaceWith(header);
          }

          const footer = nextHtml.querySelector('footer.site-footer');

          if (footer) {
            document
              .querySelector('footer.site-footer')
              ?.replaceWith(footer);
          }

          if (next.namespace === 'home') {
            const banner = nextHtml.querySelector(
              '.home-is-redirected',
            );

            if (banner) {
              document.body.insertBefore(
                banner,
                document.body.firstChild,
              );
            }
          } else {
            document.querySelector('.home-is-redirected')?.remove();
          }
        },
        afterEnter: ({ next }) => {
          if (next.url.hash) {
            document
              .querySelector(`#${next.url.hash}`)
              ?.scrollIntoView();
          } else {
            window.scrollTo(0, 0);
          }
        },
        after: async () => {
          header.refresh();
          partialHero();
          partialAccordion();
          privcy.reload();
        },
      },
    ],
  });
})();
